// Operator /operator/verification — the verify queue + anti-fraud.
// Three tabs:
//   1. Pending review — clips that creators have submitted. Operator
//      enters verified view count and confirms (which posts ledger
//      + earnings) or rejects with a reason.
//   2. Anomalies — out-of-band view deltas flagged by the rolling
//      baseline. Operator approves / discounts / rejects.
//   3. Dead-letter queue — external fetches that failed past retry budget.
//
// Pending review is the entry point — that's where new submissions land
// and where earnings get unlocked. Anomalies + DLQ are after-the-fact
// quality gates.

import { useCallback, useEffect, useState } from 'react';
import { adminApi, api, type Clip } from '../lib/api';
import { PageHeader } from '../components/Page';
import { T } from '../theme';

type Tab = 'pending' | 'anomalies' | 'dlq';

interface Anomaly {
  id: string;
  clip_id: string;
  serial_code: string;
  platform: string;
  submitted_url: string;
  campaign_id: string;
  campaign_title: string;
  delta_views: number;
  delta_window_minutes: number;
  rate_per_minute: number;
  baseline_rate: number;
  z_score: number;
  reason: string;
  created_at: string;
}

interface DLQRow {
  id: string;
  source: string;
  attempt_count: number;
  max_attempts: number;
  last_error: string;
  next_attempt_at: string | null;
}

function fmtRate(r: number): string {
  if (r < 1) return r.toFixed(2);
  if (r < 1000) return Math.round(r).toString();
  return `${(r / 1000).toFixed(1)}k`;
}

export default function Verification() {
  const [tab, setTab] = useState<Tab>('pending');
  const [data, setData] = useState<{
    anomalies: Anomaly[];
    dlq: DLQRow[];
    counts: Record<string, number>;
  } | null>(null);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const load = () => {
    adminApi
      .verificationQueue()
      .then(setData)
      .catch(() => setData({ anomalies: [], dlq: [], counts: {} }));
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  async function resolve(a: Anomaly, resolution: 'approved' | 'rejected' | 'discounted') {
    setBusy(a.id);
    try {
      await adminApi.resolveAnomaly(a.id, resolution, notes[a.id]);
      load();
    } finally {
      setBusy(null);
    }
  }

  if (!data) {
    return (
      <>
        <PageHeader
          title="Verification"
          subtitle="Pending submissions, view-delta anomalies, and the dead-letter queue."
        />
        <div style={{ padding: 32, color: T.inkMute, fontStyle: 'normal' }}>Loading…</div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Verification"
        subtitle="Pending creator submissions waiting for view counts. Plus the anomaly queue and DLQ from the rolling-baseline verifier."
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        <StatCard label="Pending review" value={pendingCount} accent={pendingCount > 0} />
        <StatCard label="Anomalies" value={data.anomalies.length} accent={data.anomalies.length > 0} color={T.warning} />
        <StatCard label="DLQ pending" value={data.dlq.length} accent={data.dlq.length > 0} color={T.error} />
        <StatCard label="Resolved (7d)" value={data.counts.resolved ?? 0} />
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: `1px solid ${T.rule}` }}>
        <TabButton active={tab === 'pending'} onClick={() => setTab('pending')}>
          Pending review {pendingCount > 0 && <CountChip n={pendingCount} />}
        </TabButton>
        <TabButton active={tab === 'anomalies'} onClick={() => setTab('anomalies')}>
          Anomalies {data.anomalies.length > 0 && <CountChip n={data.anomalies.length} />}
        </TabButton>
        <TabButton active={tab === 'dlq'} onClick={() => setTab('dlq')}>
          Dead-letter queue {data.dlq.length > 0 && <CountChip n={data.dlq.length} />}
        </TabButton>
      </div>

      {tab === 'pending' && <PendingReviewPanel onCount={setPendingCount} />}
      {tab === 'anomalies' && (
        <AnomaliesPanel
          anomalies={data.anomalies}
          busy={busy}
          notes={notes}
          setNotes={setNotes}
          resolve={resolve}
        />
      )}
      {tab === 'dlq' && <DLQPanel dlq={data.dlq} />}
    </>
  );
}

// ── Pending review tab ─────────────────────────────────────────────────────

function PendingReviewPanel({ onCount }: { onCount: (n: number) => void }) {
  const [clips, setClips] = useState<Clip[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [views, setViews] = useState<Record<string, string>>({});
  const [rejectReason, setRejectReason] = useState<Record<string, string>>({});
  const [rejecting, setRejecting] = useState<Set<string>>(new Set());

  const load = useCallback(() => {
    api.clips
      .list({ status: 'submitted' })
      .then((d) => {
        setClips(d.clips);
        onCount(d.clips.length);
      })
      .catch(() => setClips([]));
  }, [onCount]);

  useEffect(load, [load]);

  async function verify(clip: Clip) {
    const raw = (views[clip.id] ?? '').trim();
    const n = Number(raw.replace(/[,\s]/g, ''));
    if (!Number.isFinite(n) || n < 0) {
      setError('Enter a valid view count.');
      return;
    }
    setBusy(clip.id);
    setError(null);
    try {
      await api.clips.verify(clip.id, { views: Math.round(n) });
      load();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(null);
    }
  }

  async function reject(clip: Clip) {
    const reason = (rejectReason[clip.id] ?? '').trim();
    if (!reason) {
      setError('Reason required to reject.');
      return;
    }
    setBusy(clip.id);
    setError(null);
    try {
      await api.clips.verify(clip.id, { approve: false, rejection_reason: reason });
      load();
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(null);
    }
  }

  if (!clips) {
    return <div style={{ padding: 32, color: T.inkMute, fontStyle: 'normal' }}>Loading…</div>;
  }

  if (clips.length === 0) {
    return (
      <div
        style={{
          padding: 36,
          background: T.bgCard,
          border: `1px dashed ${T.rule}`,
          borderRadius: 8,
          textAlign: 'center',
          color: T.inkMute,
          fontStyle: 'normal',
        }}
      >
        Nothing to review. Every submitted clip has been verified or rejected.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {error && (
        <div
          style={{
            padding: 10,
            background: T.bgCard,
            border: `1px solid ${T.error}`,
            color: T.error,
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}
      {clips.map((c) => {
        const isRejecting = rejecting.has(c.id);
        return (
          <div
            key={c.id}
            style={{
              background: T.bgCard,
              border: `1px solid ${T.rule}`,
              borderRadius: 8,
              padding: 16,
            }}
          >
            {/* Header row */}
            <div
              style={{
                display: 'flex',
                gap: 12,
                alignItems: 'baseline',
                flexWrap: 'wrap',
                marginBottom: 8,
              }}
            >
              <span style={{ fontFamily: T.font.display, fontSize: 18, fontStyle: 'normal', color: T.ink }}>
                {c.campaign_title ?? c.serial_code}
              </span>
              {c.campaign_artist && (
                <span style={{ fontSize: 12, color: T.inkMute }}>· {c.campaign_artist}</span>
              )}
            </div>

            {/* URL + platform */}
            <div
              style={{
                fontFamily: T.font.mono,
                fontSize: 11,
                color: T.inkSoft,
                marginBottom: 12,
                wordBreak: 'break-all',
              }}
            >
              {c.serial_code}
              {c.platform && ` · ${c.platform}`}
              {' · '}
              {c.submitted_url ? (
                <a
                  href={c.submitted_url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: T.accent }}
                >
                  {c.submitted_url}
                </a>
              ) : (
                <span style={{ color: T.inkMute }}>no url</span>
              )}
              {c.submitted_at && (
                <span style={{ color: T.inkMute, marginLeft: 12 }}>
                  · submitted {c.submitted_at.slice(0, 16).replace('T', ' ')}
                </span>
              )}
            </div>

            {/* Action row: verify OR reject */}
            {!isRejecting ? (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Verified view count"
                  value={views[c.id] ?? ''}
                  onChange={(e) => setViews({ ...views, [c.id]: e.target.value })}
                  disabled={busy === c.id}
                  style={{
                    padding: '8px 12px',
                    background: T.bgSoft,
                    color: T.ink,
                    border: `1px solid ${T.rule}`,
                    borderRadius: 4,
                    fontSize: 13,
                    fontFamily: T.font.mono,
                    minWidth: 200,
                  }}
                />
                <ResolveButton
                  onClick={() => verify(c)}
                  disabled={busy === c.id || !(views[c.id] ?? '').trim()}
                  variant="live"
                >
                  {busy === c.id ? 'Verifying…' : 'Verify'}
                </ResolveButton>
                <ResolveButton
                  onClick={() => {
                    const next = new Set(rejecting);
                    next.add(c.id);
                    setRejecting(next);
                  }}
                  disabled={busy === c.id}
                  variant="error"
                >
                  Reject
                </ResolveButton>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Rejection reason (visible to clipper)"
                  value={rejectReason[c.id] ?? ''}
                  onChange={(e) => setRejectReason({ ...rejectReason, [c.id]: e.target.value })}
                  disabled={busy === c.id}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    background: T.bgSoft,
                    color: T.ink,
                    border: `1px solid ${T.rule}`,
                    borderRadius: 4,
                    fontSize: 13,
                    minWidth: 240,
                  }}
                />
                <ResolveButton
                  onClick={() => reject(c)}
                  disabled={busy === c.id || !(rejectReason[c.id] ?? '').trim()}
                  variant="error"
                >
                  {busy === c.id ? 'Rejecting…' : 'Confirm reject'}
                </ResolveButton>
                <ResolveButton
                  onClick={() => {
                    const next = new Set(rejecting);
                    next.delete(c.id);
                    setRejecting(next);
                  }}
                  disabled={busy === c.id}
                  variant="warning"
                >
                  Cancel
                </ResolveButton>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Anomalies tab (existing logic, extracted) ──────────────────────────────

function AnomaliesPanel({
  anomalies,
  busy,
  notes,
  setNotes,
  resolve,
}: {
  anomalies: Anomaly[];
  busy: string | null;
  notes: Record<string, string>;
  setNotes: (n: Record<string, string>) => void;
  resolve: (a: Anomaly, resolution: 'approved' | 'rejected' | 'discounted') => void;
}) {
  if (anomalies.length === 0) {
    return (
      <div
        style={{
          padding: 36,
          background: T.bgCard,
          border: `1px dashed ${T.rule}`,
          borderRadius: 8,
          textAlign: 'center',
          color: T.inkMute,
          fontStyle: 'normal',
        }}
      >
        Nothing flagged. Every recent view delta is within band.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {anomalies.map((a) => (
        <AnomalyCard
          key={a.id}
          a={a}
          busy={busy}
          notes={notes}
          setNotes={setNotes}
          resolve={resolve}
        />
      ))}
    </div>
  );
}

function AnomalyCard({
  a,
  busy,
  notes,
  setNotes,
  resolve,
}: {
  a: Anomaly;
  busy: string | null;
  notes: Record<string, string>;
  setNotes: (n: Record<string, string>) => void;
  resolve: (a: Anomaly, resolution: 'approved' | 'rejected' | 'discounted') => void;
}) {
  return (
    <div
      style={{
        background: T.bgCard,
        border: `1px solid ${T.warning}`,
        borderRadius: 8,
        padding: 16,
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 16,
        alignItems: 'flex-start',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontFamily: T.font.display, fontSize: 18, fontStyle: 'normal' }}>
            {a.campaign_title}
          </span>
          <span
            style={{
              fontSize: 10,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: T.warning,
            }}
          >
            {a.reason.replace(/_/g, ' ')}
          </span>
        </div>
        <div
          style={{
            fontFamily: T.font.mono,
            fontSize: 11,
            color: T.inkSoft,
            marginTop: 4,
          }}
        >
          {a.serial_code} · {a.platform} ·{' '}
          <a href={a.submitted_url} target="_blank" rel="noreferrer" style={{ color: T.accent }}>
            {a.submitted_url.slice(0, 64)}
            {a.submitted_url.length > 64 ? '…' : ''}
          </a>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, max-content)',
            columnGap: 28,
            rowGap: 4,
            marginTop: 12,
            fontSize: 12,
            color: T.inkSoft,
            fontFamily: T.font.mono,
          }}
        >
          <span>+{a.delta_views.toLocaleString()} views</span>
          <span>over {a.delta_window_minutes}m</span>
          <span>rate: {fmtRate(a.rate_per_minute)}/min</span>
          <span>z: {a.z_score.toFixed(1)}σ</span>
          <span style={{ color: T.inkMute }}>baseline: {fmtRate(a.baseline_rate)}/min</span>
        </div>
        <input
          placeholder="optional notes"
          value={notes[a.id] ?? ''}
          onChange={(e) => setNotes({ ...notes, [a.id]: e.target.value })}
          style={{
            marginTop: 10,
            padding: '6px 10px',
            background: T.bgSoft,
            color: T.ink,
            border: `1px solid ${T.rule}`,
            borderRadius: 4,
            fontSize: 12,
            width: '100%',
            maxWidth: 380,
          }}
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
        <ResolveButton onClick={() => resolve(a, 'approved')} disabled={busy === a.id} variant="live">
          Approve
        </ResolveButton>
        <ResolveButton onClick={() => resolve(a, 'discounted')} disabled={busy === a.id} variant="warning">
          Discount
        </ResolveButton>
        <ResolveButton onClick={() => resolve(a, 'rejected')} disabled={busy === a.id} variant="error">
          Reject
        </ResolveButton>
      </div>
    </div>
  );
}

// ── DLQ tab (existing logic, extracted) ────────────────────────────────────

function DLQPanel({ dlq }: { dlq: DLQRow[] }) {
  if (dlq.length === 0) {
    return (
      <div
        style={{
          padding: 28,
          background: T.bgCard,
          border: `1px dashed ${T.rule}`,
          borderRadius: 8,
          color: T.inkMute,
          fontStyle: 'normal',
          textAlign: 'center',
        }}
      >
        No stuck calls. Every external fetch in the last cycle either succeeded or retried clean.
      </div>
    );
  }
  return (
    <div style={{ background: T.bgCard, border: `1px solid ${T.rule}`, borderRadius: 8, overflow: 'hidden' }}>
      {dlq.map((d) => (
        <div
          key={d.id}
          style={{
            padding: '12px 16px',
            borderTop: `1px solid ${T.rule}`,
            display: 'grid',
            gridTemplateColumns: '1fr 80px 100px',
            gap: 12,
            fontSize: 13,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontFamily: T.font.mono,
                fontSize: 11,
                letterSpacing: '0.15em',
                color: T.inkSoft,
              }}
            >
              {d.source}
            </div>
            <div style={{ color: T.ink, marginTop: 4 }}>{d.last_error.slice(0, 200)}</div>
          </div>
          <div style={{ color: T.inkSoft, fontFamily: T.font.mono, fontSize: 12, textAlign: 'right' }}>
            {d.attempt_count}/{d.max_attempts}
          </div>
          <div style={{ color: T.inkMute, fontFamily: T.font.mono, fontSize: 11, textAlign: 'right' }}>
            {d.next_attempt_at ? new Date(d.next_attempt_at).toLocaleTimeString() : 'soon'}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Tab bar atoms ──────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '10px 16px',
        marginBottom: -1,
        background: 'transparent',
        color: active ? T.ink : T.inkMute,
        border: 'none',
        borderBottom: `2px solid ${active ? T.accent : 'transparent'}`,
        fontFamily: 'inherit',
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      {children}
    </button>
  );
}

function CountChip({ n }: { n: number }) {
  return (
    <span
      style={{
        background: T.accent,
        color: T.bg,
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 7px',
        borderRadius: 999,
        letterSpacing: '0.02em',
      }}
    >
      {n}
    </span>
  );
}


function StatCard({
  label,
  value,
  accent,
  color,
}: {
  label: string;
  value: number | string;
  accent?: boolean;
  color?: string;
}) {
  return (
    <div
      style={{
        padding: 14,
        background: T.bgCard,
        border: `1px solid ${T.rule}`,
        borderRadius: 8,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {accent && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: color ?? T.accent,
          }}
        />
      )}
      <div
        style={{
          fontSize: 10,
          letterSpacing: '0.25em',
          textTransform: 'uppercase',
          color: T.inkMute,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: T.font.display,
          fontSize: 28,
          fontWeight: 400,
          color: color && accent ? color : T.ink,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function ResolveButton({
  children,
  onClick,
  disabled,
  variant,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant: 'live' | 'warning' | 'error';
}) {
  const color = variant === 'live' ? T.live : variant === 'warning' ? T.warning : T.error;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '6px 14px',
        fontSize: 10,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        background: 'transparent',
        color,
        border: `1px solid ${color}`,
        borderRadius: 4,
        fontWeight: 600,
        cursor: disabled ? 'wait' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        minWidth: 110,
      }}
    >
      {children}
    </button>
  );
}
